import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { BUSINESS } from "../../lib/constants";

export default function AdminResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="admin-reset-password-page">
      <Helmet><title>Set New Password | Orion Services LLC</title></Helmet>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm border border-amber-500/15 rounded-md p-8 bg-carbon">
        <div className="flex items-center gap-3 mb-8">
          <img src={BUSINESS.logo} alt={BUSINESS.name} className="h-10 w-10 rounded-full object-cover" />
          <div>
            <p className="font-display text-lg text-white leading-none">ORION ADMIN</p>
            <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1"><Lock className="h-3 w-3" /> Set New Password</p>
          </div>
        </div>

        {!token ? (
          <p className="text-sm text-rose-400" data-testid="admin-reset-password-missing-token">This reset link is missing a token. Please request a new one.</p>
        ) : done ? (
          <div className="text-center py-4" data-testid="admin-reset-password-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
            <p className="text-sm text-zinc-300">Password updated. Redirecting you to login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">New Password</Label>
              <Input required type="password" data-testid="admin-reset-password-new" value={password} onChange={(e) => setPassword(e.target.value)} className="!bg-obsidian border-amber-500/15" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Confirm Password</Label>
              <Input required type="password" data-testid="admin-reset-password-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="!bg-obsidian border-amber-500/15" />
            </div>
            {error && <p className="text-rose-400 text-sm" data-testid="admin-reset-password-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="admin-reset-password-submit" className="w-full bg-amber-light text-obsidian hover:bg-white font-semibold uppercase tracking-wide">
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
